import React, { useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-date-fns';
import type { ChartOptions } from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
  zoomPlugin
);

interface NetworkHistoryItem {
  timestamp: string;
  status: string;
  connection_type?: string;
  wifi_ssid?: string;
  wifi_signal_strength?: number;
  wifi_security_type?: string;
  cellular_carrier?: string;
  cellular_network_type?: string;
  cellular_signal_strength?: number;
  ip_address?: string;
  is_metered?: boolean;
  metadata?: any;
}

interface NetworkChartProps {
  data: NetworkHistoryItem[];
  height?: number;
}

const NetworkChart: React.FC<NetworkChartProps> = ({ data, height = 300 }) => {
  const chartRef = useRef<any>(null);

  const chartData = {
    datasets: [
      {
        label: 'ネットワーク状態',
        data: data.map(item => ({
          x: new Date(item.timestamp).getTime(),
          y: item.status === 'online' ? 1 : 0,
        })),
        borderColor: '#1890ff',
        backgroundColor: 'rgba(24, 144, 255, 0.3)',
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        fill: true,
        stepped: 'after' as const,
      },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
      },
      tooltip: {
        callbacks: {
          title: (context: any) => {
            return new Date(context[0].parsed.x).toLocaleString('ja-JP');
          },
          label: (context: any) => {
            const value = context.parsed.y;
            return `状態: ${value === 1 ? 'オンライン' : 'オフライン'}`;
          },
        },
      },
      zoom: {
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          mode: 'x' as const,
        },
        pan: {
          enabled: true,
          mode: 'x' as const,
        },
      },
    },
    scales: {
      x: {
        type: 'time' as const,
        time: {
          displayFormats: {
            hour: 'MM/dd HH:mm',
            day: 'MM/dd',
          },
        },
        title: {
          display: true,
          text: '時刻',
        },
      },
      y: {
        type: 'linear' as const,
        min: 0,
        max: 1,
        ticks: {
          stepSize: 1,
          callback: function(value: any) {
            return value === 1 ? 'オンライン' : 'オフライン';
          },
        },
        title: {
          display: true,
          text: '接続状態',
        },
      },
    },
  };

  const resetZoom = () => {
    if (chartRef.current) {
      chartRef.current.resetZoom();
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 8, textAlign: 'right' }}>
        <button
          onClick={resetZoom}
          style={{
            padding: '4px 8px',
            fontSize: '12px',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            background: '#fff',
            cursor: 'pointer',
          }}
        >
          ズームリセット
        </button>
      </div>
      <div style={{ height }}>
        <Line ref={chartRef} data={chartData} options={options} />
      </div>
    </div>
  );
};

export default NetworkChart;